import { useEffect, useLayoutEffect, useState } from 'react';
import ExternalService from './external.service';
import { dataValidations, helper } from 'utils';
import { useApp } from 'context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Goods, Loader, FormSteps } from 'components';
import { setIsPurchase } from 'utils/auth';
import {
  Container,
  Box,
  Title,
  Content,
  FormStepsContainer,
  List,
  Item,
  Total,
  Section,
  Label,
  Input,
  Error,
  LastSection,
  RememberMe,
  Checkbox,
  NextButton,
  BackIcon,
  CheckoutForm,
  NextIcon,
} from './Styles';

const Checkout = () => {
  const navigate = useNavigate();
  const { state, setGlobalError, setUserData } = useApp();
  const [isLoading, setIsLoading] = useState(false);

  const { user, selectedStorePackage } = state;
  const { fNe, lNe, eml, iRd: infoRequired } = user;

  const [userDetailsForm, setUserDetailsForm] = useState({
    fNe: {
      type: 'text',
      label: 'First Name',
      placeholder: 'First Name',
      value: fNe || '',
      error: '',
      required: true,
      min: 2,
      max: 30,
    },
    lNe: {
      type: 'text',
      label: 'Last Name',
      placeholder: 'Last Name',
      value: lNe || '',
      error: '',
      required: true,
      min: 2,
      max: 30,
    },
    eml: {
      type: 'email',
      label: 'Email',
      placeholder: 'example@example.com',
      value: eml || '',
      error: '',
      required: true,
      min: 2,
      max: 50,
    },
  });

  const [currentStep, setCurrentStep] = useState(eml ? 2 : 1);
  const [isRememberMe, setIsRememberMe] = useState(infoRequired);
  const [isButtonDisabled, setIsButtonDisabled] = useState(
    Object.values(userDetailsForm).some(({ value }) => !value),
  );

  useLayoutEffect(
    () => () => {
      const { destroy } = window.checkout;
      destroy && destroy();
    },
    [],
  );

  useEffect(() => {
    if (!selectedStorePackage) {
      navigate('/', { replace: true });
    } else if (eml) {
      getCheckoutForm({ fNe, lNe, eml });
    }
  }, []);

  const onCheckoutFormSubmit = async ({ result }, iId) => {
    if (result === 'APPROVED') {
      setIsPurchase(true);
      navigate('/invoice_status/' + iId);
    }
  };

  const getCheckoutForm = async userDetails => {
    setIsLoading(true);

    const { vae, cot } = selectedStorePackage;
    const [pyi, invoice_error] = await helper.asyncWrapper(() =>
      ExternalService.createPaymenyInvoice({ vae, rMe: isRememberMe, ...userDetails }),
    );

    if (!pyi || invoice_error) {
      setGlobalError(true);
      return;
    }

    const { iId, ip, mSId, mId, tId } = pyi;
    const formConfig = {
      user: userDetails,
      ip,
      merchantSiteId: mSId,
      merchantId: mId,
      sessionToken: tId,
      amount: cot,
      onSubmit: res => onCheckoutFormSubmit(res, iId),
    };

    const [checkoutFormStatus, checkoutFormError] = await helper.asyncWrapper(() =>
      ExternalService.initCheckoutForm(formConfig),
    );

    const { sts } = checkoutFormStatus;
    if (checkoutFormError || sts !== 's') {
      setGlobalError(true);
      return;
    }

    setIsLoading(false);
    setCurrentStep(2);
    isRememberMe && setUserData({ ...user, ...userDetails });
  };

  const handleOnChange = event => {
    const { type, target } = event;
    const { name, value } = target;

    const newUserDetailsForm = { ...userDetailsForm };
    const userDetail = newUserDetailsForm[name];
    const { max } = userDetail;

    const newValue = value.replace(/\s/g, '');
    const error = dataValidations({ ...userDetail, value: newValue });

    if (type === 'blur') {
      userDetail.error = error;
    } else if (newValue.length <= max) {
      userDetail.value = newValue;
      userDetail.error = '';
      const isError =
        !!error || Object.values(newUserDetailsForm).some(({ value, error }) => !value || error);
      setIsButtonDisabled(isError);
    }

    setUserDetailsForm(newUserDetailsForm);
  };

  const handleNextStep = () => {
    const userDetails = {};
    Object.entries(userDetailsForm).forEach(([key, { value }]) => (userDetails[key] = value));
    getCheckoutForm(userDetails);
  };

  return (
    selectedStorePackage && (
      <Container>
        <Box>
          <Content>
            <Title>Preview Items</Title>
            <Goods goods={selectedStorePackage.gLt} />
          </Content>
        </Box>
        <Box>
          <Content style={{ paddingBottom: 0 }}>
            <Title>Order Summary</Title>
            <List>
              <Item>
                Game Goods X1 <strong>${selectedStorePackage.cot}</strong>
              </Item>
            </List>
          </Content>
          <Total>
            Total: <strong>${selectedStorePackage.cot}</strong>
          </Total>
        </Box>
        <Box>
          <Content>
            {infoRequired && (currentStep === 1 || !isLoading) && (
              <FormStepsContainer>
                {currentStep > 1 && <BackIcon $withTransition onClick={() => setCurrentStep(1)} />}
                <FormSteps steps={['User Details', 'Payment Method']} currentStep={currentStep} />
              </FormStepsContainer>
            )}
            {currentStep === 2 ? (
              <>
                <Title>Payment Method</Title>
                {isLoading && <Loader />}
              </>
            ) : (
              <>
                <Title>User Details</Title>
                {Object.entries(userDetailsForm).map(
                  ([key, { label, placeholder, value, error, required }]) => (
                    <Section key={key}>
                      <Label $required={required}>{label}</Label>
                      <Input
                        name={key}
                        value={value}
                        disabled={isLoading}
                        placeholder={placeholder}
                        onBlur={handleOnChange}
                        onChange={handleOnChange}
                      />
                      {error && <Error>{error}</Error>}
                    </Section>
                  ),
                )}
                <LastSection>
                  <RememberMe>
                    <Checkbox
                      checked={isRememberMe}
                      disabled={isLoading}
                      onChange={event => setIsRememberMe(event.target.checked)}
                    />
                    <Label htmlFor="remember_me">Remember me</Label>
                  </RememberMe>
                  <NextButton
                    onClick={handleNextStep}
                    disabled={isButtonDisabled || isLoading}
                    $isLoading={isLoading}
                  >
                    Next
                    {isLoading ? <Loader size={14} width={2} /> : <NextIcon />}
                  </NextButton>
                </LastSection>
              </>
            )}
            <CheckoutForm $currentStep={currentStep} />
          </Content>
        </Box>
      </Container>
    )
  );
};

export default Checkout;
